import { DateTime } from 'luxon';
import { isAdmin, isPartner } from '../../../helpers/appType';
import {
  ConversationAttachmentEntity,
  ConversationMessageAudience,
  ConversationMessageEntity,
  ConversationMessageWithStatus,
  ConversationType,
  PublicConversationType,
} from '../../../types/conversation.types';
import { isInternalRole, UserRole } from '../../../types/user.types';

/** What a message means to the person reading it: whose it is, what it says. */

/**
 * Everything the thread needs to know about who is looking at it.
 *
 * Alignment used to be a build-time constant - admin sat on `internal`, the
 * merchant on `location`, and one message was "ours" if its side matched. That
 * breaks the moment two internal users talk to each other: in a `direct` or
 * `group` conversation EVERY message is `internal`, so side says nothing about
 * authorship.
 */
export type ChatViewer = {
  /** Null while the user is still loading. Nothing is "mine" until it isn't. */
  id: number | null;
  type: ConversationType;
};

/**
 * Opens a person's detail screen.
 *
 * Passed down from the app rather than resolved here, because only the admin
 * app has such a screen - the merchant one passes nothing and the names stay
 * plain text. The role comes along because sales roles live on a different
 * route to everyone else.
 */
export type OpenUser = (userId: number, role: UserRole | null) => void;

/**
 * Which messages sit on the right.
 *
 * Two rules, and the first is the interesting one:
 *
 *   - `support`: the thread has two ends and the reader's is the whole side.
 *     On the merchant app that is `user`. On admin it is `internal`, and the
 *     vGrubs side is a COLLECTIVE - a colleague's reply belongs on the same
 *     side as your own, because to the merchant it came from the same place;
 *     you just also get to see who wrote it.
 *   - everything else: the side says nothing about who you are. Everyone in a
 *     `direct` or a `group` is internal, and a `merchantGroup` puts several
 *     restaurants on the `user` side and several colleagues on the `internal`
 *     one - so the only honest question is whether the sender is you. A
 *     `viktor` thread is private, so every internal row there IS yours - and
 *     Viktor's own rows are neither side, like a system note.
 */
export const isOwnMessage = (
  message: ConversationMessageEntity,
  viewer: ChatViewer
) => {
  if (message.side === 'system' || message.side === 'viktor') return false;
  if (viewer.type === 'support') {
    return message.side === (isPartner ? 'user' : 'internal');
  }
  /* The public kinds are support's shape with a stranger on the outside: the
     vGrubs end is one collective side, the AI included, and only admin ever
     renders them through this thread - the visitor has its own panel. */
  if (isPublicConversation(viewer.type)) {
    return message.side === (isAdmin ? 'internal' : 'user');
  }
  return viewer.id !== null && message.sender?.id === viewer.id;
};

/** Both kinds of group: the internal one and the one with merchants in it. */
export const isGroupConversation = (type?: ConversationType | null) =>
  type === 'group' || type === 'merchantGroup';

/** The kind a stranger opens without logging in: the tracker's chat. */
export const isPublicConversation = (
  type?: ConversationType | null
): type is PublicConversationType => type === 'dispatcher';

/** Who may see a message. Missing means everyone - every older kind is that. */
export const messageAudience = (
  message: ConversationMessageEntity
): ConversationMessageAudience => message.audience ?? 'everyone';

/**
 * How a dispatcher thread colours a row the customer never sees: a note, the
 * team's word to itself. Null for everything the customer does see.
 */
export type MessageTone = 'note';

export const messageTone = (
  message: ConversationMessageEntity
): MessageTone | null =>
  message.side !== 'system' && messageAudience(message) === 'internal'
    ? 'note'
    : null;

/**
 * Which team a group member speaks for: vGrubs, Sales, or the restaurant.
 *
 * The one thing a name alone does not say in a room that mixes a restaurant's
 * staff with the people supporting it - "Ana" could be either - and on the
 * merchant side, where support threads are anonymised, it is also the only
 * sign that a message came from vGrubs at all. Anything not internal and not
 * sales is a merchant role, which is every role the backend lets in besides.
 *
 * Sales is its own team to the vGrubs side only. A restaurant has one company
 * on the other side of the table, so on the merchant app a sales agent is
 * vGrubs like everybody else from it.
 */
export type GroupMemberTeam = 'vGrubs' | 'Sales' | 'Restaurant';

export const isSalesRole = (role?: UserRole | string | null) =>
  role === UserRole.salesAgent || role === UserRole.salesManager;

export const groupMemberTeam = (
  role?: UserRole | string | null
): GroupMemberTeam | null => {
  if (!role) return null;
  if (isInternalRole(role)) return 'vGrubs';
  if (isSalesRole(role)) return isPartner ? 'vGrubs' : 'Sales';
  return 'Restaurant';
};

/**
 * Who may edit or delete a message.
 *
 * Deliberately stricter than `isOwnMessage`: in a support thread every
 * colleague's reply is on your side, but the API only accepts changes to
 * messages you actually wrote, so offering the buttons on theirs would be a
 * lie. On the merchant side the sender is the outside user themselves and
 * carries a real id, so the same comparison works there.
 */
export const isAuthoredBy = (
  message: ConversationMessageEntity,
  viewer: ChatViewer
) => viewer.id !== null && message.sender?.id === viewer.id;

export const isDeleted = (message: ConversationMessageEntity) =>
  message.deletedAt !== null;

/** One of Viktor's answers, finished or still streaming. */
export const isViktorMessage = (message: ConversationMessageEntity) =>
  message.side === 'viktor';

/**
 * Viktor could not answer: the row carries the backend's error sentence in
 * place of an answer. Rendered as a note between the bubbles rather than as a
 * bubble - it is about the conversation, not part of it - and told apart by
 * the status alone, never by the text.
 */
export const isViktorFailure = (message: ConversationMessageEntity) =>
  message.side === 'viktor' && message.systemMeta?.viktorStatus === 'failed';

/**
 * Whether a message offers Translate: it has text, it is settled, and somebody
 * else wrote it. The backend would translate your own words too, but reading
 * them back in another language helps nobody. System rows and Viktor's error
 * notes are ours or the backend's sentences, not anyone's message.
 */
export const canTranslate = (
  message: ConversationMessageWithStatus,
  viewer: ChatViewer
) =>
  !!message.body?.trim() &&
  !isDeleted(message) &&
  message.side !== 'system' &&
  !message.systemEvent &&
  !isViktorFailure(message) &&
  !isAuthoredBy(message, viewer) &&
  !message.pending &&
  !message.streaming &&
  message.id > 0;

/**
 * The API returns pages newest-first; the UI reads oldest-at-top. This is the
 * single place that derivation happens - it is exactly the kind of thing that
 * gets re-implemented slightly differently in three components otherwise.
 */
export const flattenMessages = (
  pages: ConversationMessageWithStatus[][] | undefined
): ConversationMessageWithStatus[] => (pages ?? []).flat().slice().reverse();

/**
 * `salesAgent` -> "Sales Agent".
 *
 * Deliberately not `renderRole` from `helpers/helpers`: this module is reached
 * from `adminPages`, and that file pulls in `../routes`, which imports the
 * `adminPages` barrel - the cycle documented at the top of `appType.ts`. Only
 * internal roles ever reach here (a support thread's vGrubs side), so the
 * mapping is a formatting job rather than a vocabulary one.
 */
const capitalize = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (first) => first.toUpperCase());

/** The same label, for the places that hold a role rather than a message. */
export const roleLabel = (role?: string | null) =>
  role ? capitalize(role) : '';

/**
 * Label under a bubble.
 *
 * What it says depends on the kind of conversation, because the same string
 * means different things in each:
 *
 *   - `support`, seen by admin - "Katherine - Support". Which colleague
 *     answered is the whole point of a shared inbox, so the name shows on your
 *     own messages too.
 *   - `support`, seen by the merchant - whatever the backend sent, which for a
 *     vGrubs message is already the anonymised "vGrubs Support". The AI is the
 *     exception and says so, which is the one thing `isAssistant` is for.
 *   - `direct` / `group` - the other person's name, and nothing under your own
 *     bubbles: it is already on the right, and repeating your own name under
 *     every message you send is noise. A sales agent in a group says so -
 *     "Sam - Sales" - since everyone else there is a colleague.
 *   - `merchantGroup` - the same, plus the team they speak for: "Ana - Restaurant",
 *     "Marko - vGrubs". See `groupMemberTeam`.
 *
 * `system` rows never reach here - `MessageList` routes them to a note.
 */
export const ASSISTANT_LABEL = 'vGrubs AI Assistant';

export const senderLabel = (
  message: ConversationMessageEntity,
  viewer: ChatViewer,
  fallback?: string | null
) => {
  /* A collective side names every speaker on it, your own messages included -
     who answered is the point of a shared inbox. */
  if (
    viewer.type !== 'support' &&
    !isPublicConversation(viewer.type) &&
    isOwnMessage(message, viewer)
  ) {
    return '';
  }
  if (isPublicConversation(viewer.type)) {
    return publicSenderLabel(message, fallback);
  }
  /* Viktor signs his own answers - the thread is named after him, but the
     name under the run is what says these bubbles are his and not a note.
     Before the assistant branch: his messages carry `isAssistant` too, and
     inside his own thread he goes by his name. */
  if (message.side === 'viktor') return 'vGrubs AI';

  /*
   * Then every other AI message, ahead of the branches below: the merchant
   * sees the assistant under the same anonymised "vGrubs Support" name as a
   * human agent, and on admin a bot message has no sender at all, which the
   * authorless branch would call an auto reply.
   */
  if (message.isAssistant) return ASSISTANT_LABEL;
  if (!message.sender) {
    /*
     * An internal message with no author is the system talking - the support
     * auto reply. Only the vGrubs side ever sees an authorless internal:
     * for the merchant the backend anonymises every internal sender to a
     * named "vGrubs Support" entity. Without this branch the fallback took
     * over, and the admin page's fallback is the conversation's own name -
     * which attributed the auto reply to the very merchant it answered.
     */
    if (!isPartner && message.side === 'internal') return 'Auto Reply';
    return fallback ?? '';
  }

  const { name, role } = message.sender;

  if (viewer.type === 'merchantGroup') {
    const team = groupMemberTeam(role);
    return team
      ? `${name || fallback || ''} - ${team}`
      : name || fallback || '';
  }

  /* By role, not side: sales writes as `user`, but so would a merchant, and
     an internal group has none. */
  if (viewer.type === 'group' && isSalesRole(role)) {
    return `${name || fallback || ''} - Sales`;
  }

  // Role disambiguates two Marks, and only makes sense on the vGrubs side.
  if (
    !isPartner &&
    viewer.type === 'support' &&
    message.side === 'internal' &&
    role
  ) {
    return `${name} - ${capitalize(role)}`;
  }
  return name || fallback || '';
};

/** The AI, a colleague or the customer - and whether a row was a note. */
const publicSenderLabel = (
  message: ConversationMessageEntity,
  fallback?: string | null
) => {
  const who = message.isAssistant
    ? message.sender?.name || ASSISTANT_LABEL
    : message.side === 'internal'
    ? message.sender?.role
      ? `${message.sender.name} - ${capitalize(message.sender.role)}`
      : message.sender?.name || 'vGrubs'
    : message.sender?.name || fallback || 'Customer';

  return messageTone(message) === 'note' ? `${who} - Note` : who;
};

/**
 * Same voice: same side, same author, however long the pause between them.
 *
 * This is the only thing a run is keyed on. An earlier version also broke the
 * run after five quiet minutes, which put an uneven gap in the middle of one
 * person's messages and repeated their name - a pause is not a change of
 * speaker, and each message carries its own timestamp anyway.
 *
 * `system` rows never match - they are notes, not conversation. A deleted one
 * does: it keeps its sender, so it belongs to the same run, and excluding it
 * only cut the run in three and wrote the name out under each piece.
 *
 * Two null sender ids compare equal, which is intended and safe: a message
 * with no sender is the anonymised vGrubs entity on the merchant side or the
 * support auto reply on the admin side, and within one side each of those IS
 * one speaker. Everywhere a group could put two different people on the same
 * side - either app's groups - the backend sends real ids.
 */
export const isSameAuthorAs = (
  message: ConversationMessageWithStatus,
  previous?: ConversationMessageWithStatus
) => {
  if (!previous) return false;
  if (message.side === 'system' || previous.side === 'system') return false;
  /* A failed answer renders as a note, and a note between two bubbles breaks
     the run the way a system row does. */
  if (isViktorFailure(message) || isViktorFailure(previous)) return false;
  if (message.side !== previous.side) return false;
  /* A note beside a customer-facing message is a different voice even from
     the same person: it carries its own label. */
  if (messageTone(message) || messageTone(previous)) {
    return false;
  }
  return (message.sender?.id ?? null) === (previous.sender?.id ?? null);
};

/** True when `message` opens a new calendar day relative to `previous`. */
export const startsNewDay = (
  message: ConversationMessageEntity,
  previous?: ConversationMessageEntity
) => {
  if (!previous) return true;
  return !DateTime.fromISO(message.createdAt).hasSame(
    DateTime.fromISO(previous.createdAt),
    'day'
  );
};

/**
 * Splits the two layouts an attachment can take: a picture or a clip is shown
 * as media (and can carry a caption), while a voice note or a document is a
 * self-contained pill that never goes inside a bubble.
 */
export const isVisualAttachment = (attachment: ConversationAttachmentEntity) =>
  attachment.type === 'image' || attachment.type === 'video';

/** Preview text for an inbox row or a reply strip with no body. */
export const attachmentPlaceholder = (message: ConversationMessageEntity) => {
  const first = message.attachments[0];
  if (!first) return '';
  switch (first.type) {
    case 'image':
      return 'Photo';
    case 'video':
      return 'Video';
    case 'audio':
      return 'Voice message';
    default:
      return first.name || 'Attachment';
  }
};

/** One line standing in for a message: its body, or what it carries instead. */
export const messagePreview = (message: ConversationMessageEntity) =>
  message.body || attachmentPlaceholder(message);

/**
 * What a notification calls a message: who wrote it, and where.
 *
 * The two are the same answer in a direct message, whose name IS the person on
 * the other end - so it says a name once. A group and a support thread are
 * named after something other than their sender, and there the sender alone
 * would leave the reader guessing which conversation they have been pulled
 * into, so both go in.
 *
 * "New message" only survives when the sender could not be named AND the
 * thread could not either, which now takes two failed lookups rather than one
 * missing field.
 */
export const conversationHeading = ({
  sender,
  thread,
  mention,
}: {
  sender?: string | null;
  thread?: { type: ConversationType; name: string };
  mention?: boolean;
}) => {
  const who = sender || (thread?.type === 'direct' ? thread.name : '');
  const place = thread && thread.type !== 'direct' ? thread.name : '';
  // A support thread is named after the person on the other end, so when they
  // are the sender the two are one name - and saying it twice reads as a bug.
  const where = place === who ? '' : place;

  if (mention) {
    return `${who || 'Someone'} mentioned you${where ? ` in ${where}` : ''}`;
  }
  return [who, where].filter(Boolean).join(' · ') || 'New message';
};

/**
 * DOM id for a message row, so a reply strip can find the message it quotes -
 * and so the scroll anchoring can hold onto a row by name rather than by node.
 * Keyed on the server id - optimistic rows have no reply target yet.
 */
export const CHAT_MESSAGE_DOM_PREFIX = 'chat-message-';

export const chatMessageDomId = (id: number) =>
  `${CHAT_MESSAGE_DOM_PREFIX}${id}`;

/**
 * DOM id of the "New messages" divider, so opening a thread can land the view
 * on the divider itself rather than approximating its place from the row below
 * it - the pill carries its own margins, and they belong in the picture.
 */
export const CHAT_UNREAD_DIVIDER_ID = 'chat-unread-divider';
